import { useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { RoutePoint, ThemeClasses } from "../../types";
import { LINEAR_BUFFER_MIN, LINEAR_BUFFER_MAX } from "../../types";

interface RoutePointsTableProps {
  routePoints: RoutePoint[];
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onCoordinateEdit: (index: number, field: "latitude" | "longitude", raw: string) => void;
  onAddressEdit: (index: number, rawAddress: string) => void;
  onAddPoint: () => void;
  onRemovePoint: (index: number) => void;
  firstPointRadius: number;
  lastPointRadius: number;
  onFirstPointRadiusChange: (value: number) => void;
  onLastPointRadiusChange: (value: number) => void;
  corridorBuffer: number;
  onCorridorBufferChange: (value: number) => void;
  radiusErrors: { first?: boolean; last?: boolean };
}

function getRole(index: number, total: number): "start" | "waypoint" | "end" {
  if (index === 0) return "start";
  if (index === total - 1) return "end";
  return "waypoint";
}

const ROLE_META = {
  start: {
    label: "Origen",
    bg: "bg-emerald-500",
    text: "text-white",
    dot: "bg-emerald-500",
  },
  waypoint: {
    label: "Vía",
    bg: "bg-amber-400",
    text: "text-white",
    dot: "bg-amber-400",
  },
  end: {
    label: "Destino",
    bg: "bg-red-500",
    text: "text-white",
    dot: "bg-red-500",
  },
};

function CoordInput({
  value,
  onCommit,
  isDarkMode,
  themeClasses,
  placeholder,
}: {
  value: number;
  onCommit: (raw: string) => void;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  placeholder: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleBlur = () => {
    const raw = inputRef.current?.value ?? "";
    onCommit(raw);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      inputRef.current?.blur();
    }
  };

  return (
    <input
      ref={inputRef}
      type="number"
      step="any"
      defaultValue={value.toFixed(6)}
      key={value.toFixed(6)}
      placeholder={placeholder}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`w-full text-xs px-2 py-1.5 rounded-md border ${themeClasses.cardBorder} ${
        isDarkMode ? "bg-dark-surface text-gray-200" : "bg-white text-gray-800"
      } focus:outline-none focus:ring-2 focus:ring-primary-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
    />
  );
}

function AddressInput({
  value,
  onCommit,
  isDarkMode,
  themeClasses,
  disabled,
}: {
  value: string;
  onCommit: (raw: string) => void;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  disabled: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const pickedFromDropdown = useRef(false);

  const commitAddress = useCallback(
    (address: string) => {
      if (address.trim() && address.trim() !== (value ?? "").trim()) {
        onCommit(address);
      }
    },
    [onCommit, value]
  );

  useEffect(() => {
    const input = inputRef.current;
    if (
      !input ||
      typeof google === "undefined" ||
      !google.maps?.places?.Autocomplete
    )
      return;

    if (autocompleteRef.current) return;

    const ac = new google.maps.places.Autocomplete(input, {
      types: ["geocode", "establishment"],
      fields: ["formatted_address"],
    });

    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (place?.formatted_address) {
        pickedFromDropdown.current = true;
        if (inputRef.current) {
          inputRef.current.value = place.formatted_address;
        }
        commitAddress(place.formatted_address);
      }
    });

    autocompleteRef.current = ac;

    return () => {
      google.maps.event.clearInstanceListeners(ac);
      autocompleteRef.current = null;
    };
  }, [commitAddress]);

  const handleBlur = () => {
    if (pickedFromDropdown.current) {
      pickedFromDropdown.current = false;
      return;
    }
    const raw = inputRef.current?.value ?? "";
    commitAddress(raw);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      inputRef.current?.blur();
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      defaultValue={value}
      key={value}
      placeholder="Escribe una dirección..."
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={`w-full text-xs px-2 py-1 rounded-md border ${themeClasses.cardBorder} ${
        isDarkMode ? "bg-dark-surface text-gray-200" : "bg-white text-gray-800"
      } focus:outline-none focus:ring-2 focus:ring-primary-500 placeholder:text-gray-400 placeholder:italic disabled:opacity-50`}
    />
  );
}

export function RouteCoordinatesForm({
  routePoints,
  themeClasses,
  isDarkMode,
  onCoordinateEdit,
  onAddressEdit,
  onAddPoint,
  onRemovePoint,
  firstPointRadius,
  lastPointRadius,
  onFirstPointRadiusChange,
  onLastPointRadiusChange,
  corridorBuffer,
  onCorridorBufferChange,
  radiusErrors,
}: RoutePointsTableProps) {
  const { t } = useTranslation("adminSections");

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          Puntos de la ruta
        </label>
        {routePoints.length < 2 && (
          <span className="text-red-500 text-xs font-medium">
            Mínimo 2 puntos requeridos
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {routePoints.map((point, idx) => {
          const role = getRole(idx, routePoints.length);
          const meta = ROLE_META[role];
          const canRemove = routePoints.length > 2;

          return (
            <div
              key={idx}
              className={`rounded-xl border ${themeClasses.cardBorder} ${
                isDarkMode ? "bg-dark-card" : "bg-gray-50"
              } overflow-hidden`}
            >
              {/* Row header */}
              <div className="flex items-center gap-2 px-3 pt-2.5 pb-1.5">
                {/* Role badge */}
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.bg} ${meta.text}`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full bg-white/70`}
                  />
                  {meta.label}
                </span>

                {/* Address — editable input */}
                <div className="flex-1 min-w-0 relative">
                  {point.geocoding && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 inline-block w-3 h-3 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
                  )}
                  <AddressInput
                    value={point.address ?? ""}
                    onCommit={(raw) => onAddressEdit(idx, raw)}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                    disabled={point.geocoding}
                  />
                </div>

                {/* Remove button */}
                <button
                  type="button"
                  onClick={() => onRemovePoint(idx)}
                  disabled={!canRemove}
                  title={canRemove ? "Eliminar punto" : "Mínimo 2 puntos requeridos"}
                  className={`flex-shrink-0 p-1 rounded-md transition-colors ${
                    canRemove
                      ? "text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      : "text-gray-300 cursor-not-allowed"
                  }`}
                >
                  <i className="ri-close-line text-base" />
                </button>
              </div>

              {/* Coordinate inputs */}
              <div className="flex gap-2 px-3 pb-2.5">
                <div className="flex-1 flex items-center gap-1.5">
                  <span className="text-[10px] font-semibold text-gray-400 w-6 shrink-0">
                    Lat
                  </span>
                  <CoordInput
                    value={point.latitude}
                    onCommit={(raw) => onCoordinateEdit(idx, "latitude", raw)}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                    placeholder="Latitud"
                  />
                </div>
                <div className="flex-1 flex items-center gap-1.5">
                  <span className="text-[10px] font-semibold text-gray-400 w-6 shrink-0">
                    Lng
                  </span>
                  <CoordInput
                    value={point.longitude}
                    onCommit={(raw) => onCoordinateEdit(idx, "longitude", raw)}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                    placeholder="Longitud"
                  />
                </div>
              </div>

              {/* Radius input for origin / destination */}
              {(role === "start" || role === "end") && (
                <div className={`flex items-center gap-2 px-3 pb-2.5 border-t ${themeClasses.cardBorder} pt-2`}>
                  <i className={`ri-radius-line text-sm ${role === "start" ? "text-emerald-500" : "text-red-500"}`} />
                  <span className="text-[10px] font-semibold text-gray-400 shrink-0">
                    Radio (m)
                  </span>
                  <input
                    type="number"
                    min={50}
                    max={10000}
                    step={50}
                    value={role === "start" ? firstPointRadius : lastPointRadius}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (role === "start") onFirstPointRadiusChange(v);
                      else onLastPointRadiusChange(v);
                    }}
                    className={`w-full text-xs px-2 py-1.5 rounded-md border ${
                      (role === "start" && radiusErrors.first) || (role === "end" && radiusErrors.last)
                        ? "ring-2 ring-red-500"
                        : "focus:ring-2 focus:ring-primary-500"
                    } ${themeClasses.cardBorder} ${
                      isDarkMode ? "bg-dark-surface text-gray-200" : "bg-white text-gray-800"
                    } focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
                    placeholder="500"
                  />
                  {((role === "start" && radiusErrors.first) || (role === "end" && radiusErrors.last)) && (
                    <span className="text-red-500 text-[10px] whitespace-nowrap">50–10000</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onAddPoint}
        className={`flex items-center justify-center gap-2 w-full py-2 rounded-lg border border-dashed text-sm font-medium transition-colors ${
          isDarkMode
            ? "border-dark-border text-gray-400 hover:border-primary-500 hover:text-primary-400"
            : "border-gray-300 text-gray-500 hover:border-primary-500 hover:text-primary-600"
        }`}
      >
        <i className="ri-add-line" />
        Agregar punto intermedio
      </button>

      <div
        className={`flex flex-col gap-2 rounded-xl border ${themeClasses.cardBorder} ${
          isDarkMode ? "bg-dark-card" : "bg-gray-50"
        } p-3`}
      >
        <div className="flex items-center justify-between">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("routes.corridor.label", "Ancho del corredor")}
          </label>
          <span className={`text-xs ${themeClasses.textSecondary}`}>
            {t("routes.corridor.value", {
              defaultValue: "{{buffer}} m a cada lado ({{total}} m de ancho)",
              buffer: corridorBuffer,
              total: corridorBuffer * 2,
            })}
          </span>
        </div>
        <input
          type="range"
          min={LINEAR_BUFFER_MIN}
          max={LINEAR_BUFFER_MAX}
          step={10}
          value={corridorBuffer}
          onChange={(e) => onCorridorBufferChange(Number(e.target.value))}
          className="w-full accent-primary-500"
        />
        <span className="text-[11px] text-gray-400 leading-relaxed">
          {t(
            "routes.corridor.help",
            "Distancia máxima al trazado para considerar que la unidad va dentro de la ruta. Salirse de este corredor es lo que dispara la alerta de desvío."
          )}
        </span>
      </div>
    </div>
  );
}
